import ApplicationController from '@aha-app/mvc';
import type WhiteboardController from '../../controllers/WhiteboardController';
import StylusLinkObjectSelection from '../../stylus/StylusLinkObjectSelection';
import { type LinkToUrlBehaviorsInterface } from '../../objects/LinkToUrlBehaviors';

export interface UrlInputModalControllerProps {
  sourceObject: fabric.Object & LinkToUrlBehaviorsInterface;
  whiteboardController: WhiteboardController;
}

class UrlInputModalController extends ApplicationController<
  {
    urlFromSelection: string | null;
    urlFromHovering?: string;
  },
  UrlInputModalControllerProps
> {
  static initialState = {
    urlFromSelection: null,
    urlFromHovering: null,
  };

  urlSelectionModalTarget: fabric.Object | undefined;
  urlSelectionModalHoveredObject: fabric.Object | null;

  get sourceObject() {
    return this.props.sourceObject;
  }

  private get whiteboardController() {
    return this.props.whiteboardController;
  }

  async initialize() {
    this.state.urlFromSelection = this.sourceObject.linkToUrl ?? null;
    this.urlSelectionModalTarget = this.sourceObject.getObjectLinkedByUrl();

    const selectionStylus = new StylusLinkObjectSelection({
      controller: this.whiteboardController.proxiedThis,
      canvas: this.whiteboardController.canvas,
      urlInputModalController: this,
    });
    this.whiteboardController.actionSetStylus(selectionStylus);
  }

  actionHideModal() {
    this.whiteboardController.state.urlInputModalObject = undefined;
    this.whiteboardController.actionFocus();
    this.whiteboardController.actionResetStylus();
    // Set the current selection back to the object that was selected
    // when we entered this mode.
    this.whiteboardController.canvas.setActiveObject(this.sourceObject);
    this.whiteboardController.canvas.requestRenderAll();
  }

  actionConfirm() {
    const validatedUrl = this.validatedUrl();
    if (validatedUrl) {
      this.sourceObject.set('linkToUrl', validatedUrl);
    }

    this.actionHideModal();
  }

  actionClear() {
    this.sourceObject.set('linkToUrl', '');
    this.actionHideModal();
  }

  /** Validate the url and return a properly formatted version, or null if invalid */
  validatedUrl(): string | null {
    let validatedUrl = this.state.urlFromSelection;

    if (!validatedUrl) {
      return null;
    }

    // Check if URL has protocol, if not add https://
    if (!validatedUrl.match(/^https?:\/\//)) {
      validatedUrl = `https://${validatedUrl}`;
    }

    try {
      // This will throw an error if the URL is invalid
      void new URL(validatedUrl);
    } catch {
      // URL is invalid, don't hide the modal
      return null;
    }

    return validatedUrl;
  }

  actionHoverCurrentTarget() {
    const newTarget = this.whiteboardController.canvas._target;

    if (!newTarget || newTarget === this.sourceObject) {
      this.state.urlFromHovering = undefined;
      this.urlSelectionModalHoveredObject = null;
    } else {
      this.urlSelectionModalHoveredObject = newTarget;
      this.state.urlFromHovering = this.whiteboardController.getObjectsUrl([
        this.urlSelectionModalHoveredObject,
      ]);
    }
  }

  // Handler for object selection in URL selection mode
  actionHandleUrlSelectionClick() {
    const target = this.whiteboardController.canvas._target;

    // If we clicked on an object with an ID
    if (target?.id && target !== this.sourceObject) {
      this.urlSelectionModalTarget = target;

      // Create a URL that points to this object
      const objectUrl = this.whiteboardController.getObjectsUrl([target]);

      if (typeof objectUrl === 'string') {
        this.state.urlFromSelection = objectUrl;
      }
    }
  }

  hoverTargetUrl() {
    if (this.urlSelectionModalHoveredObject) {
      return this.whiteboardController.getObjectsUrl([
        this.urlSelectionModalHoveredObject,
      ]);
    }

    return null;
  }
}

export default UrlInputModalController;
